import { useState } from "react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";
import { Check, Upload, X, Ruler, ImagePlus, CircleCheck, CircleAlert, Info } from "lucide-react";

const SPACES = ["Basement", "Garage", "Shop", "Shed", "Commercial", "Other"];
const BUDGETS = [
  "Under $15K",
  "$15K–$25K",
  "$25K–$40K",
  "$40K+",
  "Not sure yet",
];
const MAX_PHOTOS = 6;
const MAX_BYTES = 10 * 1024 * 1024;

export default function QuoteForm({ buildConfig, onCleared }) {
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    space_type: "Basement",
    budget_range: "$25K–$40K",
    message: "",
  });
  const [dims, setDims] = useState({
    length_ft: "",
    depth_ft: "",
    height_ft: "",
    height_in: "",
  });
  const [photos, setPhotos] = useState([]); // [{ path, url, name, size }]
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setDim = (k) => (e) => setDims({ ...dims, [k]: e.target.value });

  const onPickFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_PHOTOS} photos.`);
      return;
    }
    const batch = files.slice(0, remaining);
    setUploading(true);
    for (const file of batch) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is over 10 MB.`);
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/uploads/space-photo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [
          ...prev,
          {
            path: data.path,
            url: `${BACKEND_URL}${data.url}`,
            name: file.name,
            size: data.size,
          },
        ]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Upload failed for ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) =>
    setPhotos((prev) => prev.filter((p) => p.path !== path));

  const dimsPayload = () => {
    const any = ["length_ft", "depth_ft", "height_ft", "height_in"].some(
      (k) => dims[k] !== "" && dims[k] != null,
    );
    if (!any) return null;
    const num = (v) => (v === "" || v == null ? null : Number(v));
    return {
      length_ft: num(dims.length_ft),
      depth_ft: num(dims.depth_ft),
      height_ft: num(dims.height_ft),
      height_in: num(dims.height_in),
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/leads", {
        ...f,
        build_config: buildConfig,
        space_dimensions: dimsPayload(),
        space_photos: photos.map((p) => p.path),
      });
      setSuggestions(data.suggestions || []);
      setDone(true);
      onCleared?.();
      toast.success("Quote request sent. We'll reach out shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section
        id="quote-form"
        data-testid="quote-form-section"
        className="py-24 lg:py-32 border-t border-neutral-900"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex w-16 h-16 items-center justify-center border border-[#F5F5F0] mb-6">
            <Check size={28} />
          </div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            Got it. We&apos;ll be in touch.
          </h2>
          <p className="mt-4 text-neutral-400">
            Brad will follow up personally within one business day. Urgent?
            Call{" "}
            <a href="tel:4033935772" className="underline">
              403-393-5772
            </a>
            .
          </p>

          {suggestions.length > 0 && (
            <div
              data-testid="space-suggestions"
              className="mt-12 text-left border border-neutral-800 p-6 md:p-8 bg-neutral-950"
            >
              <p className="font-mono-label text-[11px] text-neutral-500 mb-4">
                Quick read on your space
              </p>
              <ul className="space-y-4">
                {suggestions.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <SuggestionIcon level={s.level} />
                    <div>
                      <p className="font-display font-semibold">{s.title}</p>
                      <p className="text-sm text-neutral-400 mt-1">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-6 border-t border-neutral-800 text-xs text-neutral-500 font-mono-label">
                Automated read only. Brad will confirm on the walkthrough.
              </p>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      id="quote-form"
      data-testid="quote-form-section"
      className="py-24 lg:py-32 border-t border-neutral-900"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono-label text-[11px] text-neutral-500 mb-4">
            06 · Request a Free Quote
          </p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Book an in-person walkthrough.
          </h2>
          <p className="mt-6 text-neutral-400">
            No obligation. No high-pressure sales pitch. We look at the space,
            price it honestly, and you decide.
          </p>
          {buildConfig && (
            <div
              data-testid="attached-build-banner"
              className="mt-6 border border-[#F5F5F0] p-4"
            >
              <p className="font-mono-label text-[10px] text-neutral-400">
                Attached selections
              </p>
              <p className="font-display font-bold text-xl mt-1">
                {buildConfig.items?.length} components chosen
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                Brad will price the full build after seeing your space.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-2 gap-4">
          <Field2 label="Name" required testid="quote-input-name" value={f.name} onChange={set("name")} />
          <Field2 label="Email" required type="email" testid="quote-input-email" value={f.email} onChange={set("email")} />
          <Field2 label="Phone" required testid="quote-input-phone" value={f.phone} onChange={set("phone")} />
          <Field2 label="City" required testid="quote-input-location" value={f.location} onChange={set("location")} />
          <SelectField label="Space type" testid="quote-input-space" value={f.space_type} onChange={set("space_type")} options={SPACES} />
          <SelectField label="Budget range" testid="quote-input-budget" value={f.budget_range} onChange={set("budget_range")} options={BUDGETS} />

          <div className="col-span-2">
            <label className="font-mono-label text-[10px] text-neutral-500">
              Tell us about the space
            </label>
            <textarea
              rows="3"
              data-testid="quote-input-message"
              value={f.message}
              onChange={set("message")}
              className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0] resize-none"
            />
          </div>

          {/* Space dimensions + photos */}
          <div
            data-testid="space-block"
            className="col-span-2 border border-neutral-800 p-5 md:p-6 bg-neutral-950 mt-2"
          >
            <div className="flex items-start gap-3 mb-4">
              <Ruler size={18} className="mt-0.5 text-[#F5F5F0]" />
              <div>
                <p className="font-display font-semibold text-base">
                  Your Space (optional)
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  A quick sketch of the room + a few photos lets Brad pre-plan
                  the build.
                </p>
              </div>
            </div>

            <div className="mb-4 border border-dashed border-neutral-700 p-3 text-xs text-neutral-400 flex items-start gap-2">
              <Info size={14} className="mt-0.5 shrink-0" />
              <p>
                <span className="text-[#F5F5F0] font-semibold">The Sim Guys sweet-spot:</span>{" "}
                15&apos; wide, 23&apos; deep, minimum 9&apos;5&quot; ceiling. Smaller
                works — we&apos;ll adapt.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <DimInput label="Width (ft)" testid="dim-length" value={dims.length_ft} onChange={setDim("length_ft")} />
              <DimInput label="Depth (ft)" testid="dim-depth" value={dims.depth_ft} onChange={setDim("depth_ft")} />
              <DimInput label="Height (ft)" testid="dim-height-ft" value={dims.height_ft} onChange={setDim("height_ft")} />
              <DimInput label="Height (in)" testid="dim-height-in" value={dims.height_in} onChange={setDim("height_in")} min={0} max={11} />
            </div>

            <div className="mt-5">
              <label
                className="flex flex-col items-center justify-center border border-dashed border-neutral-700 py-6 px-4 cursor-pointer hover:border-[#F5F5F0] transition-colors"
                data-testid="photo-upload-label"
              >
                <input
                  type="file"
                  multiple
                  accept="image/*,image/heic,image/heif"
                  className="hidden"
                  data-testid="photo-upload-input"
                  onChange={(e) => {
                    onPickFiles(e.target.files);
                    e.target.value = "";
                  }}
                  disabled={uploading || photos.length >= MAX_PHOTOS}
                />
                <ImagePlus size={20} className="text-neutral-400" />
                <p className="mt-2 font-mono-label text-[10px] text-neutral-400">
                  {uploading
                    ? "Uploading…"
                    : photos.length >= MAX_PHOTOS
                    ? `Max ${MAX_PHOTOS} photos`
                    : "Tap to add photos of the space"}
                </p>
                <p className="mt-1 text-[10px] text-neutral-600">
                  Up to {MAX_PHOTOS} · 10 MB each · JPG / PNG / HEIC
                </p>
              </label>

              {photos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="photo-grid">
                  {photos.map((p) => (
                    <div key={p.path} className="relative group aspect-square border border-neutral-800 overflow-hidden">
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(p.path)}
                        data-testid={`remove-photo-${p.path.split("/").pop()}`}
                        className="absolute top-1 right-1 bg-black/80 text-[#F5F5F0] w-6 h-6 flex items-center justify-center hover:bg-black"
                        aria-label="remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || uploading}
            data-testid="quote-form-submit-btn"
            className="col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-[#F5F5F0] text-black py-4 font-semibold hover:bg-[#EAEAE5] transition-colors disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send Quote Request"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field2({ label, required, type = "text", testid, value, onChange }) {
  return (
    <div className="col-span-2 md:col-span-1">
      <label className="font-mono-label text-[10px] text-neutral-500">{label}</label>
      <input
        required={required}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0]"
      />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div className="col-span-2 md:col-span-1">
      <label className="font-mono-label text-[10px] text-neutral-500">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full mt-2 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0]"
      >
        {options.map((s) => (
          <option key={s} value={s} className="bg-black">
            {s}
          </option>
        ))}
      </select>
    </div>
  );
}

function DimInput({ label, testid, value, onChange, min = 0, max }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] text-neutral-500">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={label.includes("in") ? "1" : "0.5"}
        min={min}
        max={max}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0]"
      />
    </label>
  );
}

function SuggestionIcon({ level }) {
  if (level === "ok")
    return <CircleCheck size={18} className="mt-0.5 text-[#F5F5F0] shrink-0" />;
  if (level === "warn")
    return <CircleAlert size={18} className="mt-0.5 text-[#F5F5F0] shrink-0" />;
  return <Info size={18} className="mt-0.5 text-neutral-400 shrink-0" />;
}
